import React, { useEffect, useState } from "react"
import { Container, Row, Col, Button, Card, Form } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useAuth } from "../components/auth/useAuth"
import { getPaymentMethods, getReceipts } from "../actions/other/receiptsActions"
import { RootState, AppDispatch } from "../store"
import { CustomDataTable } from "../components/design/Datatables/CustomDataTable"
import Loader from "../components/loaders/Loader"

export interface Receipt {
    _id?: string
    id?: string
    idCliente?: string
    idPoliza?: string
    tipo?: string
    clase?: string
    nifCliente?: string
    ordinal?: string
    numero?: string
    fechaEfecto?: string
    estado?: string
    primaTotal?: number
    comisionCorreduria?: string
    nombreCompletoTomador?: string
    medioCobro?: string,
    hsp?: number
}

export default function Receipts() {
    const { t } = useTranslation()
    const dispatch: AppDispatch = useDispatch()
    const navigate = useNavigate()
    const { clientauth } = useAuth()

    const { receipts, loading, paymentMethods, total } = useSelector((state: RootState) => state.receipts)

    const [page, setPage] = useState<number>(1)
    const [limit, setLimit] = useState<number>(25)
    const [totalRows, setTotalRows] = useState(0)
    const [search, setSearch] = useState<any>({})
    const [sort, setSort] = useState<any>(null)

    const [nifCliente, setNifCliente] = useState("")
    const [numero, setNumero] = useState("")
    const [primaTotal, setPrimaTotal] = useState("")
    const [ordinal, setOrdinal] = useState("")
    const [fechaEfecto, setFechaEfecto] = useState("")
    const [estado, setEstado] = useState("")
    const [medioCobro, setMedioCobro] = useState("")

    const [optionsPaymentMethods, setOptionsPaymentMethods] = useState<{ value: string, label: string }[]>([])

    useEffect(() => {
        if (clientauth) {
            dispatch(getPaymentMethods())
        }
    // eslint-disable-next-line
    }, [dispatch, clientauth])


    useEffect(() => {
        if (paymentMethods) {
            setOptionsPaymentMethods(paymentMethods.map((method: any) => ({ value: method.ebrokerId, label: method.name })))
            setMedioCobro("")
        }
    }, [paymentMethods])

    const handleSearch = () => {
        dispatch(getReceipts(1, 999999, search) as any)
    }

    useEffect(() => {
        setTotalRows(total || 0)
    }, [total])

    const selectReceipt = (receipt: Receipt) => {
        navigate(`${receipt.id}`)
    }

    const columnsReceipt = [
        {
            name: t("receiptTranslate.numero"),
            id: "numero",
            grow: 1,
            cell: (receipt: Receipt) => (
                <div className="cursor" onClick={() => selectReceipt(receipt)}>
                    {receipt?.numero || "—"}
                </div>
            ),
            selector: (receipt: Receipt) => receipt?.numero || "—",
            sortable: true,
        }, {
            name: t("receiptTranslate.nombreCompletoTomador"),
            id: "nombreCompletoTomador",
            grow: 2,
            cell: (receipt: Receipt) => (
                <div className="cursor" onClick={() => selectReceipt(receipt)}>
                    {receipt?.nombreCompletoTomador || "—"}
                </div>
            ),
            selector: (receipt: Receipt) => receipt?.nombreCompletoTomador || "—",
            sortable: true,
        }, {
            name: t("receiptTranslate.fechaEfecto"),
            id: "fechaEfecto",
            grow: 1,
            cell: (receipt: Receipt) => (
                <div className="cursor" onClick={() => selectReceipt(receipt)}>
                    {(() => {
                        if (!receipt?.fechaEfecto) return "—";
                        const raw = receipt.fechaEfecto;
                        // Intentamos arreglar años con más de 4 dígitos
                        const fixed = raw.replace(/^(\d{4})\d+/, "$1");
                        const date = new Date(fixed);
                        if (isNaN(date.getTime())) return "—";
                        return new Intl.DateTimeFormat('es-ES', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            timeZone: 'Europe/Madrid'
                        }).format(date);
                    })()}
                </div>
            ),
            selector: (receipt: Receipt) => receipt?.fechaEfecto || "—",
            sortable: true,
        }, {
            name: t("receiptTranslate.estado"),
            id: "estado",
            cell: (receipt: Receipt) => (
                <div className="cursor" onClick={() => selectReceipt(receipt)}>
                    {receipt?.estado || "—"}
                </div>
            ),
            selector: (receipt: Receipt) => receipt?.estado || "—",
            sortable: true,
        },{
            id: "primaTotal",
            name: t("receiptTranslate.importeTotal"),
            grow: 1,
            cell: (receipt: Receipt) => (
                <div className="cursor" onClick={() => selectReceipt(receipt)}>
                    {((receipt?.primaTotal ?? 0) + (receipt?.hsp ?? 0)).toFixed(2) + " €" || "—"}
                </div>
            ),
            selector: (receipt: Receipt) => receipt?.primaTotal || "—",
            sortable: true,
        }

    ]

    return (
        <Container fluid className="p-1 py-5 p-md-5">
            <Row>
                <Col xs={12}>
                    <h1>{t("menus.receipts")}</h1>
                </Col>
            </Row>

            {/* BUSCADOR */}
            <Row>
                <Col xs={12} className="my-3">
                    <Card>
                        <Card.Body>
                            <Row className="justify-content-between">
                                <Col xs={10}>
                                    <Row>
                                        <Col xs={12} lg={3} className="d-flex mb-3">
                                            <Form.Control
                                                placeholder={t("receiptTranslate.ordinal")}
                                                value={ordinal}
                                                onChange={e => {
                                                    setOrdinal(e.target.value)
                                                    setSearch({ ...search, ordinal: e.target.value })
                                                }}
                                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                    if (e.key === "Enter") {
                                                        setSearch({ ...search, ordinal: ordinal });
                                                        setPage(1);
                                                        handleSearch();
                                                    }
                                                }}
                                            />
                                        </Col>
                                        <Col xs={12} lg={3} className="d-flex mb-3">
                                            <Form.Control
                                                placeholder={t("receiptTranslate.numero")}
                                                value={numero}
                                                onChange={e => {
                                                    setNumero(e.target.value)
                                                    setSearch({ ...search, numero: e.target.value })
                                                }}
                                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                    if (e.key === "Enter") {
                                                        setSearch({ ...search, numero: numero });
                                                        setPage(1);
                                                        handleSearch();
                                                    }
                                                }}
                                            />
                                        </Col>
                                        <Col xs={12} lg={2} className="d-flex mb-3">
                                            <Form.Control
                                                placeholder={t("receiptTranslate.primaTotal")}
                                                value={primaTotal}
                                                onChange={e => {
                                                    setPrimaTotal(e.target.value)
                                                    setSearch({ ...search, primaTotal: e.target.value })
                                                }}
                                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                    if (e.key === "Enter") {
                                                        setSearch({ ...search, primaTotal: primaTotal });
                                                        setPage(1);
                                                        handleSearch();
                                                    }
                                                }}
                                            />
                                        </Col>
                                        <Col xs={12} lg={4} className="d-flex mb-3">
                                            <Form.Control
                                                className="text-uppercase"
                                                placeholder={t("receiptTranslate.nifCliente")}
                                                value={nifCliente}
                                                onChange={e => {
                                                    const val = e.target.value.toUpperCase();
                                                    setNifCliente(val)
                                                    setSearch({ ...search, nifCliente: val })
                                                }}
                                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                    if (e.key === "Enter") {
                                                        setSearch({ ...search, nifCliente: nifCliente });
                                                        setPage(1);
                                                        handleSearch();
                                                    }
                                                }}
                                            />
                                        </Col>
                                        <Col xs={12} lg={4} className="d-flex">
                                            <Form.Control
                                                placeholder={t("receiptTranslate.fechaEfecto")}
                                                value={fechaEfecto}
                                                onChange={e => {
                                                    setFechaEfecto(e.target.value)
                                                    setSearch({ ...search, fechaEfecto: e.target.value })
                                                }}
                                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                    if (e.key === "Enter") {
                                                        setSearch({ ...search, fechaEfecto: fechaEfecto });
                                                        setPage(1);
                                                        handleSearch();
                                                    }
                                                }}
                                            />
                                        </Col>
                                        <Col xs={12} lg={4} className="d-flex">
                                            <Form.Select
                                                value={estado}
                                                onChange={e => {
                                                    setEstado(e.target.value)
                                                    setSearch({ ...search, estado: e.target.value })
                                                }}
                                            >
                                                <option value="">{t("receiptTranslate.status.sinEstado")}</option>
                                                <option value="PROCESANDO_DATOS">{t("receiptTranslate.status.procesandoDatos")}</option>
                                                <option value="ANULADO">{t("receiptTranslate.status.anulado")}</option>
                                                <option value="ANULADO_DEVUELTO_CIA">{t("receiptTranslate.status.anuladoDevueltoCia")}</option>
                                                <option value="COBRADO">{t("receiptTranslate.status.cobrado")}</option>
                                                <option value="COBRADO_LIQUIDADO">{t("receiptTranslate.status.cobradoLiquidado")}</option>
                                                <option value="COBRADO_PENDIENTE_RIESGO_BANCARIO">{t("receiptTranslate.status.cobradoPendienteRiesgoBancario")}</option>
                                                <option value="COBRADO_CIA">{t("receiptTranslate.status.cobradoCia")}</option>
                                                <option value="PENDIENTE">{t("receiptTranslate.status.pendiente")}</option>
                                                <option value="PENDIENTE_DEVUELTO_CIA">{t("receiptTranslate.status.pendienteDevueltoCia")}</option>
                                                <option value="PENDIENTE_ENTE">{t("receiptTranslate.status.pendienteEnte")}</option>
                                                <option value="PENDIENTE_DEVUELTO_BANCO">{t("receiptTranslate.status.pendienteDevueltoBanco")}</option>
                                                <option value="PENDIENTE_DEVUELTO_BANCO_ENTE">{t("receiptTranslate.status.pendienteDevueltoBancoEnte")}</option>
                                                <option value="PENDIENTE_LIQUIDADO">{t("receiptTranslate.status.pendienteLiquidado")}</option>
                                                <option value="PENDIENTE_LIQUIDADO_ENTE">{t("receiptTranslate.status.pendienteLiquidadoEnte")}</option>
                                            </Form.Select>
                                        </Col>
                                        <Col xs={12} lg={4} className="d-flex">
                                            <Form.Select
                                                value={medioCobro}
                                                className="select"
                                                onChange={e => {
                                                    setMedioCobro(e.target.value)
                                                    setSearch({ ...search, medioCobro: e.target.value })
                                                }}
                                            >
                                                <option value="">{t("receiptTranslate.paymentMethodNotSelected")}</option>

                                                {optionsPaymentMethods.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col xs={2} className="text-end">
                                    <Button onClick={handleSearch}>
                                        <i className="fas fa-search" />
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Card>
                        <Card.Body>
                            {loading ? (
                                <Loader />
                            ) : receipts?.length > 0 ? (
                                <CustomDataTable
                                    columns={columnsReceipt}
                                    data={receipts}
                                    pagination
                                />
                            ) : (
                                <h4 className="mt-4">{t("receiptTranslate.thereisnotreceipt")}</h4>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    )
}